import { createRef } from "react"
import { createRoot, type Root } from "react-dom/client"
import type { Editor, Range } from "@tiptap/core"
import type { SuggestionOptions } from "@tiptap/suggestion"
import { fixedPosInPortal } from "@/components/ui/dropdown-menu-position"
import { wasOpenedViaDoubleSpace, clearDoubleSpaceTrigger } from "./double-space-mention"
import { MentionList, type MentionAsyncItems, type MentionListHandle } from "./mention-list"
import { shouldInsertSpaceAfterMention } from "./mention-insert-space"
import { mentionAttrsFromItem, mentionInMenu, type MentionCategory, type MentionItem } from "./types"

/** Module flag: only one prompt editor mounts at a time (shot panel / improve dialog). */
let mentionSuggestionActive = false

export function isMentionSuggestionActive(): boolean {
  return mentionSuggestionActive
}

export function filterMentionItems(items: MentionItem[], query: string): MentionItem[] {
  const q = query.trim().toLowerCase()
  const withoutCam = items.filter((it) => mentionInMenu(it))
  const matched = q
    ? withoutCam.filter((it) => it.label.toLowerCase().includes(q) || (it.subtitle ?? "").toLowerCase().includes(q))
    : withoutCam
  return matched.slice(0, 40)
}

type RenderProps = {
  editor: Editor
  range: Range
  query: string
  command: (item: { category: string; id: number; label: string }) => void
  clientRect?: (() => DOMRect | null) | null
  items: MentionItem[]
}

export type MentionPlacement = "auto" | "above" | "below"

const MENTION_MENU_MAX_HEIGHT = 256
const MENTION_GAP = 6
/** Fallback before first paint — keep small so placement="above" does not jump ~256px up. */
const MENTION_MENU_HEIGHT_FALLBACK = 40

export function createMentionSuggestion(
  getItems: () => MentionItem[],
  getPlacement: () => MentionPlacement = () => "below",
  getOnMentionInsert: () => ((item: MentionItem) => void) | undefined = () => undefined,
  {
    captureEscape = true,
    asyncItems,
    categoryOrder,
    allow,
    excludeNodeIds,
  }: {
    captureEscape?: boolean
    asyncItems?: () => MentionAsyncItems | null
    categoryOrder?: MentionCategory[]
    /** When false, `@` stays plain text (composer without a node catalog). */
    allow?: () => boolean
    /** Node ids already in the document — hidden from the board @ menu. */
    excludeNodeIds?: (editor: Editor) => ReadonlySet<number>
  } = {},
): Omit<SuggestionOptions<MentionItem>, "editor"> {
  return {
    char: "@",
    allowSpaces: false,
    ...(allow ? { allow: () => allow() } : {}),
    items: ({ query, editor }) => {
      const excluded = excludeNodeIds?.(editor)
      const source =
        excluded == null ? getItems() : getItems().filter((item) => item.category !== "node" || !excluded.has(item.id))
      return filterMentionItems(source, query)
    },
    command: ({ editor, range, props }) => {
      const item = props as MentionItem
      const attrs = mentionAttrsFromItem(item)
      const insertSpace = shouldInsertSpaceAfterMention(editor.state.doc, range.to)
      editor
        .chain()
        .focus()
        .insertContentAt(range, [
          { type: "mention", attrs },
          ...(insertSpace ? [{ type: "text" as const, text: " " }] : []),
        ])
        .run()
      getOnMentionInsert()?.(item)
    },
    render: () => {
      let root: Root | null = null
      let container: HTMLDivElement | null = null
      const listRef = createRef<MentionListHandle>()
      let editorRef: RenderProps["editor"] | null = null
      let currentRange: Range | null = null
      let captureEscapeHandler: ((e: KeyboardEvent) => void) | null = null
      let positionFrame = 0
      let latestClientRect: RenderProps["clientRect"] = null
      let resizeObserver: ResizeObserver | null = null
      let portalHost: HTMLElement | null = null

      const position = (clientRect?: (() => DOMRect | null) | null) => {
        if (!container || !clientRect) return
        const rect = clientRect()
        if (!rect) return

        const host = portalHost ?? document.body
        const hostBox = host === document.body ? null : host.getBoundingClientRect()
        const frameW = hostBox?.width ?? window.innerWidth
        const frameTop = hostBox?.top ?? 0
        const frameLeft = hostBox?.left ?? 0
        const frameBottom = hostBox?.bottom ?? window.innerHeight

        const measuredHeight = container.offsetHeight
        const menuHeight =
          measuredHeight > 0 ? Math.min(measuredHeight, MENTION_MENU_MAX_HEIGHT) : MENTION_MENU_HEIGHT_FALLBACK
        const placement = getPlacement()
        const spaceBelow = frameBottom - rect.bottom - MENTION_GAP
        const spaceAbove = rect.top - frameTop - MENTION_GAP
        const openAbove =
          placement === "above" || (placement === "auto" && spaceBelow < menuHeight && spaceAbove > spaceBelow)

        const menuWidth = container.offsetWidth || 288
        const viewportTop = openAbove
          ? Math.max(frameTop + MENTION_GAP, rect.top - menuHeight - MENTION_GAP)
          : rect.bottom + MENTION_GAP
        const viewportLeft = Math.min(
          Math.max(frameLeft + MENTION_GAP, rect.left),
          frameLeft + frameW - menuWidth - MENTION_GAP,
        )
        const { top, left } = fixedPosInPortal({ top: viewportTop, left: viewportLeft }, host)
        container.style.top = `${top}px`
        container.style.left = `${left}px`
      }

      const schedulePosition = (clientRect?: (() => DOMRect | null) | null) => {
        latestClientRect = clientRect
        position(clientRect)
        // Re-measure after React paints — flushSync breaks TipTap's suggestion lifecycle.
        cancelAnimationFrame(positionFrame)
        positionFrame = requestAnimationFrame(() => {
          position(latestClientRect)
          positionFrame = requestAnimationFrame(() => position(latestClientRect))
        })
      }

      const renderList = (props: RenderProps) => {
        if (!root) return
        root.render(
          <MentionList
            ref={listRef}
            items={props.items}
            query={props.query}
            command={props.command}
            asyncItems={asyncItems?.() ?? undefined}
            categoryOrder={categoryOrder}
          />,
        )
      }

      return {
        onStart: (props: RenderProps) => {
          mentionSuggestionActive = true
          editorRef = props.editor
          currentRange = props.range

          // Перехватываем Escape на window (capture) раньше чем Radix Dialog,
          // который слушает document с { capture: true }.
          captureEscapeHandler = (e: KeyboardEvent) => {
            if (e.key !== "Escape") return
            if (!currentRange || !editorRef) return
            e.stopImmediatePropagation()
            const noQuery = currentRange.to - currentRange.from === 1
            if (wasOpenedViaDoubleSpace(editorRef) && noQuery) {
              // Удаляем пробел перед @ (вставленный триггером) + сам @, восстанавливаем 2 пробела
              editorRef
                .chain()
                .deleteRange({ from: currentRange.from - 1, to: currentRange.to })
                .insertContent("  ")
                .run()
            } else if (wasOpenedViaDoubleSpace(editorRef)) {
              // Двойной пробел + запрос набран: пользователь начал mention, но отменил.
              // Удаляем пробел перед @ + @query — восстанавливать пробелы не нужно.
              editorRef.commands.deleteRange({ from: currentRange.from - 1, to: currentRange.to })
            } else {
              // Пользователь сам напечатал @: удаляем только @query, стандартная отмена suggestion.
              editorRef.commands.deleteRange(currentRange)
            }
          }
          if (captureEscape) window.addEventListener("keydown", captureEscapeHandler, { capture: true })

          container = document.createElement("div")
          container.className = "pe-mention-popup"
          container.style.position = "fixed"
          container.style.zIndex = "300"
          // Монтируем в ближайший Radix-диалог, иначе pointerdown по попапу считается
          // кликом «снаружи» и закрывает всю панель шота.
          portalHost = (props.editor.view.dom.closest('[role="dialog"]') as HTMLElement | null) ?? document.body
          portalHost.appendChild(container)
          resizeObserver = new ResizeObserver(() => position(latestClientRect))
          resizeObserver.observe(container)
          root = createRoot(container)
          renderList(props)
          schedulePosition(props.clientRect)
        },
        onUpdate: (props: RenderProps) => {
          currentRange = props.range
          renderList(props)
          schedulePosition(props.clientRect)
        },
        onKeyDown: (props: { event: KeyboardEvent; range: Range }) => {
          // Третий пробел сразу после двойного (запрос пустой) — отменяем триггер,
          // удаляем вставленный « @» и восстанавливаем 3 пробела.
          if (props.event.key === " " && wasOpenedViaDoubleSpace(editorRef!)) {
            const noQuery = props.range.to - props.range.from === 1
            if (noQuery) {
              editorRef!
                .chain()
                .deleteRange({ from: props.range.from - 1, to: props.range.to })
                .insertContent("   ")
                .run()
              return true
            }
          }
          return listRef.current?.onKeyDown(props.event) ?? false
        },
        onExit: () => {
          mentionSuggestionActive = false
          cancelAnimationFrame(positionFrame)
          positionFrame = 0
          latestClientRect = null
          resizeObserver?.disconnect()
          resizeObserver = null
          if (captureEscapeHandler) {
            window.removeEventListener("keydown", captureEscapeHandler, { capture: true })
            captureEscapeHandler = null
          }
          if (editorRef) clearDoubleSpaceTrigger(editorRef)
          editorRef = null
          currentRange = null
          root?.unmount()
          root = null
          container?.remove()
          container = null
          portalHost = null
        },
      }
    },
  }
}
